import React, { forwardRef, useCallback, useEffect, useId, useRef, useState } from "react";
import Icon from "./Icon";
import IconButton from "./IconButton";
import Toggle from "./Toggle";
import { formatMoneyMajor } from "../../core/currency";
import "./ListingCard.scss";

// Fontaine M35 — ListingCard (`ft-lcard`). The one composite whose appearance is NOT negotiable
// (charter, "Components whose appearance is not negotiable"): the first Style Guide V2 listing-card
// variant — toggles on the photo, a frozen 468×332 internal design, ON/OFF and RSVD labels.
//
// FROZEN CONTRACT, in one place so a reviewer can check it without reading the CSS:
//   · The card is exactly CARD_W × CARD_H. Every internal band has a fixed height, so no font
//     metric, translation or missing field can change the card's size.
//   · ListingCardGrid scales the WHOLE card uniformly into its column. It never reflows: there is
//     no phone layout of this card, only a smaller one.
//   · The model title is one line with an ellipsis, always.
//   · Only the TOP region (info + photo) flips. The status row and the footer stay put.
//   · Toggles auto-save — the change persists the moment it is made. There is no Save button.
//
// Colour and type come from the production semantic tokens, which resolve to the same values the
// guide's `--v2-*` aliases do; the frozen pixel geometry lives in the `--ft-lcard-*` component
// tokens at the top of ListingCard.scss.

/** The frozen internal design. Exported so the grid, the tests and the guide share one source. */
export const CARD_W = 468;
export const CARD_H = 332;

const num = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v));
const sum = (rows, key) => rows.reduce((total, row) => total + (num(row?.[key]) ?? 0), 0);

// Money is formatted here rather than through <Money> on purpose: <Money> owns its own type scale,
// and the card's type scale is part of the frozen design. Same formatter, card-owned typography.
const cash = (amount, currency) =>
  amount == null ? "—" : formatMoneyMajor(amount, { currency, decimals: 0 }).replace(/^-/, "−");

const percent = (part, whole) =>
  whole ? `${part >= 0 ? "+" : "−"}${Math.abs(Math.round((part / whole) * 1000) / 10)}%` : "";

const clampPercent = (part, whole) =>
  whole ? Math.max(0, Math.min(100, Math.round((part / whole) * 100))) : 0;

/**
 * The deal ledger on the back. RULE (approved V2 back face): nothing the front already says
 * appears here, and no figure appears twice. Four blocks — stock, money in vs profit, the linked
 * sale, and who reserved it. Every block is optional: a partial ledger renders the parts it has.
 */
function DealLedger({ ledger, listPrice, currency }) {
  const costs = Array.isArray(ledger.costs) ? ledger.costs.filter(Boolean) : [];
  const purchase = num(ledger.purchase);
  const costsTotal = sum(costs, "amount");
  const moneyIn = purchase == null && !costs.length ? null : (purchase ?? 0) + costsTotal;
  const market = num(ledger.marketValue);
  const list = num(listPrice);

  // Profit is quoted at market when PriceIQ has a value, else at list. With neither, there is no
  // profit to quote — the block says so rather than inventing a zero.
  const profitAtList = list != null && moneyIn != null ? list - moneyIn : null;
  const profitAtMarket = market != null && moneyIn != null ? market - moneyIn : null;
  const headline = profitAtMarket ?? profitAtList;
  const margin = headline != null && moneyIn ? percent(headline, moneyIn) : "";

  const sale = ledger.sale || null;
  const collected = num(sale?.collected);
  const collectedPct = clampPercent(collected ?? 0, list);
  const stockStatus = ledger.stockStatus || null;
  const statusSlug = stockStatus ? stockStatus.toLowerCase().replace(/\s+/g, "-") : null;
  const where = [ledger.cabinet, ledger.serial ? `S/N ${ledger.serial}` : null].filter(Boolean);

  return (
    <div className="ft-lcard__ledger">
      <div className="ft-lcard__ledger-head">
        <span className="ft-lcard__eyebrow">Deal ledger</span>
        <span className="ft-lcard__stock">
          {stockStatus ? (
            <span className={`ft-lcard__stock-status ft-lcard__stock-status--${statusSlug}`}>{stockStatus}</span>
          ) : null}
          {where.length ? <span className="ft-lcard__stock-where">{where.join(" · ")}</span> : null}
        </span>
      </div>

      <div className="ft-lcard__hero">
        <div className="ft-lcard__kpi">
          <span className="ft-lcard__slabel">Money in</span>
          <span className="ft-lcard__kpi-value">{cash(moneyIn, currency)}</span>
        </div>
        <div className="ft-lcard__kpi ft-lcard__kpi--profit">
          <span className="ft-lcard__slabel">
            {market != null ? `Potential profit · market ${cash(market, currency)}` : "Profit at list"}
          </span>
          <span className="ft-lcard__kpi-row">
            <span
              className={`ft-lcard__kpi-value ft-lcard__kpi-value--profit${headline != null && headline < 0 ? " is-loss" : ""}`}
            >
              {cash(headline, currency)}
            </span>
            {margin ? <span className="ft-lcard__margin">{margin}</span> : null}
          </span>
          {market != null && profitAtList != null ? (
            <span className="ft-lcard__kpi-sub">at list {cash(profitAtList, currency)}</span>
          ) : null}
        </div>
      </div>

      {purchase != null || costs.length ? (
        <ul className="ft-lcard__rows">
          {purchase != null ? (
            <li className="ft-lcard__row">
              <span className="ft-lcard__row-label">Purchase</span>
              <span />
              <span className="ft-lcard__row-amount">{cash(purchase, currency)}</span>
            </li>
          ) : null}
          {costs.map((cost, i) => (
            <li className="ft-lcard__row ft-lcard__row--cost" key={cost.label || i}>
              <span className="ft-lcard__row-label">{cost.label}</span>
              <span />
              <span className="ft-lcard__row-amount">{cash(num(cost.amount) ?? 0, currency)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {sale ? (
        <div className="ft-lcard__sale">
          <div className="ft-lcard__sale-head">
            <span className="ft-lcard__slabel">{sale.kind || "Linked sale"}</span>
            {sale.number ? <span className="ft-lcard__sale-no">{sale.number}</span> : null}
            {sale.client ? <span className="ft-lcard__sale-client">{sale.client}</span> : null}
          </div>
          <div className="ft-lcard__collect">
            {/* Semantic progress, not a styled div: the value IS the meaning here, so the element
                carries it and no inline width is needed. */}
            <progress
              className="ft-lcard__collect-bar"
              value={collectedPct}
              max={100}
              aria-label={`${collectedPct}% of the sale collected`}
            />
            <span className="ft-lcard__collect-text">
              <span>
                Collected <b>{cash(collected, currency)}</b>{" "}
                {sale.collectedOn ? <span className="ft-lcard__when">{sale.collectedOn}</span> : null}
              </span>
              <span>
                Due <b className="is-due">{cash(num(sale.due), currency)}</b>{" "}
                {sale.dueState ? <span className="ft-lcard__pill">{sale.dueState}</span> : null}
              </span>
            </span>
          </div>
        </div>
      ) : null}

      {ledger.reservedBy ? (
        <div className="ft-lcard__facts">
          <div className="ft-lcard__fact">
            <span className="ft-lcard__slabel">Reserved by</span>
            <span className="ft-lcard__fact-value">{ledger.reservedBy}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * ListingCard — the pixel-frozen V2 watch card.
 *
 * @param {string} brand / {string} [reference] / {string} model      front line 1 and 2
 * @param {number|null} [price]                                       major units; null → "Price on request"
 * @param {string} [currency]                                         ISO code; defaults to the store base
 * @param {string|number} [year] / {string} [condition]               front line 3
 * @param {string} [image] / {string} [imageAlt]                      photo; omitted → the empty photo well
 * @param {string|number} [marketplaceCount]                          e.g. "1/3" — the footer count
 * @param {boolean} [active=false]                                    CONTROLLED — the ON/OFF toggle
 * @param {boolean} [reserved=false]                                  CONTROLLED — the RSVD toggle
 * @param {(next: boolean) => void|Promise<void>} [onActiveChange]     auto-saves; return a promise for
 *                                                                    optimistic update + rollback
 * @param {(next: boolean) => void|Promise<void>} [onReservedChange]   same
 * @param {Function} [onEdit] / [onOpen] / [onMarketplaces]            front actions
 * @param {boolean} [flipped]                                          CONTROLLED flip; omit for internal state
 * @param {(next: boolean) => void} [onFlippedChange]                  fires on every flip, controlled or not
 * @param {object} [ledger]                                           back-face data; omitted → no flip control
 * @param {boolean} [marketplaceOpen]                                drives the footer button's aria-expanded / chevron
 * @param {string} [marketplaceLabel="Marketplaces"]                 the footer button's accessible name, for a caller
 *                                                                    that puts another panel behind it
 * @param {"start"|"end"} [marketplacePanelAlign="start"]            which edge the panel opens from
 * @param {{photoOverlay?: ReactNode, backFace?: ReactNode, marketplaceAdornment?: ReactNode, marketplacePanel?: ReactNode, infoLine?: ReactNode, priceAdornment?: ReactNode}} [slots]
 *                                                                    bounded live-adapter anchors;
 *                                                                    backFace and ledger are exclusive;
 *                                                                    infoLine replaces the year · condition
 *                                                                    line (same height, so geometry holds)
 * @param {boolean} [disabled=false]                                   locks both toggles; a toggle
 *                                                                    whose change handler is absent
 *                                                                    is locked on its own too
 */
const ListingCard = forwardRef(function ListingCard(
  {
    brand,
    reference,
    model,
    price,
    currency,
    year,
    condition,
    image,
    imageAlt,
    marketplaceCount,
    active = false,
    reserved = false,
    onActiveChange,
    onReservedChange,
    onEdit,
    onOpen,
    onMarketplaces,
    marketplaceOpen = false,
    marketplaceLabel = "Marketplaces",
    marketplacePanelAlign = "start",
    flipped: flippedProp,
    onFlippedChange,
    ledger,
    slots = {},
    disabled = false,
    className = "",
    ...rest
  },
  ref
) {
  const uid = useId().replace(/:/g, "");
  const [internalFlipped, setInternalFlipped] = useState(false);
  // Controlled when `flipped` is supplied, internal otherwise — and a ledger-less card can never
  // be flipped, so a caller cannot land on a blank back.
  const { photoOverlay, backFace, marketplaceAdornment, marketplacePanel, infoLine, priceAdornment } = slots;
  const canFlip = Boolean(ledger || backFace);
  const flipped = canFlip && (flippedProp === undefined ? internalFlipped : Boolean(flippedProp));

  const toggleFlip = () => {
    const next = !flipped;
    if (flippedProp === undefined) setInternalFlipped(next);
    onFlippedChange?.(next);
  };

  // The toggles auto-save, so a controlled switch with no change handler could only ever be a
  // focusable no-op: flip it and nothing is written. Rather than expose that, the card locks any
  // switch it has no way to persist. The explicit `disabled` prop still locks both.
  const activeLocked = disabled || typeof onActiveChange !== "function";
  const reservedLocked = disabled || typeof onReservedChange !== "function";

  const listPrice = num(price);
  const name = [brand, model, reference].filter(Boolean).join(" ");
  const classes = ["ft-lcard", flipped ? "is-flipped" : "", className].filter(Boolean).join(" ");

  return (
    <article ref={ref} className={classes} aria-label={name || "Listing"} {...rest}>
      <div className="ft-lcard__flip">
        <div className="ft-lcard__face ft-lcard__face--front" aria-hidden={flipped || undefined}>
          <div className="ft-lcard__info">
            <div className="ft-lcard__meta-row">
              {brand ? <span className="ft-lcard__meta">{brand}</span> : null}
              {brand && reference ? <span className="ft-lcard__sep" aria-hidden="true">|</span> : null}
              {reference ? <span className="ft-lcard__meta">{reference}</span> : null}
            </div>
            <div className="ft-lcard__name-row">
              <span className="ft-lcard__model">{model}</span>
              <span className="ft-lcard__price-wrap">
                {priceAdornment ?? null}
                {active ? <span className="ft-lcard__dot" role="img" aria-label="Active listing" /> : null}
                <span className={`ft-lcard__price${listPrice == null ? " ft-lcard__price--por" : ""}`}>
                  {listPrice == null ? "Price on request" : cash(listPrice, currency)}
                </span>
              </span>
            </div>
            {infoLine ? (
              <div className="ft-lcard__meta-row ft-lcard__meta-row--slot">{infoLine}</div>
            ) : (
              <div className="ft-lcard__meta-row">
                {year ? <span className="ft-lcard__meta ft-lcard__meta--year">{year}</span> : null}
                {year && condition ? <span className="ft-lcard__sep" aria-hidden="true">|</span> : null}
                {condition ? <span className="ft-lcard__meta ft-lcard__meta--cond">{condition}</span> : null}
              </div>
            )}
          </div>

          <div className="ft-lcard__photo">
            {image ? <img src={image} alt={imageAlt ?? name} loading="lazy" decoding="async" /> : null}
            {photoOverlay ? (
              <div className="ft-lcard__photo-overlay" inert={flipped ? "" : undefined}>
                {photoOverlay}
              </div>
            ) : null}
            <div className="ft-lcard__icons">
              {onEdit ? (
                <IconButton
                  className="ft-lcard__icon"
                  label="Edit"
                  size="sm"
                  onClick={onEdit}
                  tabIndex={flipped ? -1 : undefined}
                >
                  <Icon name="Pencil" size="xs" />
                </IconButton>
              ) : null}
              {onOpen ? (
                <IconButton
                  className="ft-lcard__icon"
                  label="Open"
                  size="sm"
                  onClick={onOpen}
                  tabIndex={flipped ? -1 : undefined}
                >
                  <Icon name="ArrowRight" size="xs" />
                </IconButton>
              ) : null}
            </div>
          </div>
        </div>

        {canFlip ? (
          <div
            className="ft-lcard__face ft-lcard__face--back"
            id={`${uid}-ledger`}
            aria-hidden={!flipped || undefined}
            inert={!flipped ? "" : undefined}
          >
            {backFace || <DealLedger ledger={ledger} listPrice={listPrice} currency={currency} />}
          </div>
        ) : null}
      </div>

      {/* Fixed band 1 — the two auto-saving toggles, lifted onto the photo. Never flips. */}
      <div className="ft-lcard__status">
        <Toggle
          className="ft-lcard__toggle"
          tone="active"
          checked={active}
          disabled={activeLocked}
          onChange={onActiveChange}
          aria-label="Active"
          statusOn="ON"
          statusOff="OFF"
        />
        <Toggle
          className="ft-lcard__toggle"
          tone="reserved"
          checked={reserved}
          disabled={reservedLocked}
          onChange={onReservedChange}
          aria-label="Reserved"
          labelPosition="start"
          statusOn="RSVD"
          statusOff="RSVD"
        />
      </div>

      {/* Fixed band 2 — flip on the left, marketplace count on the right. Never flips. */}
      <div className="ft-lcard__footrow">
        {canFlip ? (
          <button
            type="button"
            className={`ft-lcard__foot ft-lcard__foot--left${flipped ? " is-on" : ""}`}
            aria-label={flipped ? "Show listing" : "Show deal ledger"}
            aria-pressed={flipped}
            aria-controls={`${uid}-ledger`}
            onClick={toggleFlip}
          >
            <Icon name="RefreshCw" size="sm" />
          </button>
        ) : (
          <span className="ft-lcard__foot ft-lcard__foot--left is-empty" aria-hidden="true" />
        )}
        <button
          type="button"
          className="ft-lcard__foot ft-lcard__foot--right"
          aria-label={marketplaceLabel}
          aria-expanded={marketplaceOpen}
          onClick={onMarketplaces}
          disabled={!onMarketplaces}
        >
          {marketplaceCount === null ? null : (
            <span className="ft-lcard__count">{marketplaceCount ?? "—"}</span>
          )}
          {marketplaceAdornment ? (
            <span className="ft-lcard__marketplace-adornment">{marketplaceAdornment}</span>
          ) : null}
          <Icon name="ChevronDown" size="xs" />
        </button>
      </div>

      {/* The marketplace panel lives inside the card, not in a body portal: it opens from the
          bottom-left of the frame and the card's own `overflow: hidden` keeps it inside. */}
      {marketplacePanel ? (
        <div className={`ft-lcard__market-panel${marketplacePanelAlign === "end" ? " ft-lcard__market-panel--end" : ""}`}>
          {marketplacePanel}
        </div>
      ) : null}
    </article>
  );
});

export default ListingCard;

/**
 * ListingCardCell — one grid cell. It measures its own width and publishes the uniform scale
 * factor as `--ft-lcard-scale` on itself; the CSS does the rest. The measurement is written
 * straight to the node rather than held in React state so a resize drag costs no re-render, and
 * so the card needs no inline style attribute at all.
 */
export function ListingCardCell({ children, className = "", ...rest }) {
  const node = useRef(null);
  const measure = useCallback((el) => {
    if (!el) return;
    // Snap the measured width to a whole device pixel before deriving the scale factor.
    // `transform: scale` rasterizes the card fresh at its scaled size, but a fractional
    // result (near-certain from a fluid grid's fr-unit widths) lands text/border edges
    // between device pixels, which the browser can't anti-alias crisply — the blur this
    // was reported against. Snapping trades at most half a device pixel of width for a
    // sharp card; on a >1x DPR display (the common case) that's imperceptible.
    const dpr = window.devicePixelRatio || 1;
    const snappedWidth = Math.round(el.clientWidth * dpr) / dpr;
    // 6 decimal places, not 4 — at 4 the scale's own rounding could reintroduce up to
    // ~0.1 device px of the error the snap above just removed.
    const scale = Math.round((snappedWidth / CARD_W) * 1e6) / 1e6;
    el.style.setProperty("--ft-lcard-scale", String(scale || 1));
  }, []);

  useEffect(() => {
    const el = node.current;
    if (!el) return undefined;
    measure(el);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => measure(el));
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <div className={`ft-lcard-cell ${className}`.trim()} ref={node} {...rest}>
      <div className="ft-lcard-cell__inner">{children}</div>
    </div>
  );
}

/**
 * ListingCardGrid — auto-fill columns, one uniformly scaled card per cell. Every cell in a row is
 * the same width, so every card is the same size: the "cards are always identical" rule holds by
 * construction rather than by discipline.
 *
 * @param {number} [minColumn=360]   smallest column the grid will create before dropping a column
 */
export function ListingCardGrid({ children, minColumn = 360, className = "", ...rest }) {
  return (
    // The column minimum is a caller-chosen layout input, not a design value, so it cannot come
    // from a token. It feeds grid-template-columns through the one custom property the stylesheet
    // reads — the only style attribute in this file.
    // eslint-disable-next-line no-restricted-syntax -- caller-chosen grid column minimum
    <div className={`ft-lcard-grid ${className}`.trim()} style={{ "--ft-lcard-min": `${minColumn}px` }} {...rest}>
      {React.Children.map(children, (child, i) =>
        child ? <ListingCardCell key={child.key ?? i}>{child}</ListingCardCell> : null
      )}
    </div>
  );
}
